import { useDispatch } from 'react-redux';
import { ExtendedGetResult } from '@fingerprintjs/fingerprintjs-pro-spa';
import { useHistory } from 'react-router-dom';
import { useSelector, RootStateOrAny } from 'react-redux';
import { SocialAuthCallbackResponse } from '../../interfaces';
import { setAuth, setIsLoading } from '../../../store/auth/actions';
import { setUser } from '../../../store/user/actions';
import useAuth from '../../../services/auth/useAuth';
import { SignInMethod } from '../../enums/Auth';
import { useErrorHandler } from '../../../utils';
import { setFSUser } from '../../../services/fullstory';

export default function useSocialAuthCallback() {
  // Calls Auth BE to verify Google OAuth user using 2fa.
  const dispatch = useDispatch();
  const { handleUserRedirect, verifySocialAuth } = useAuth();
  const history = useHistory();
  const {
    deviceDetails,
    fingerPrintDetails: fingerPrintData,
    redirectFrom,
    redirectTo,
  } = useSelector((state: RootStateOrAny) => state.auth);
  const { handleError, throwAlert } = useErrorHandler();

  async function onSocialAuthCallback(OAuthResponse: Partial<SocialAuthCallbackResponse>, closed?: boolean) {
    try {
      const { token, userDetails, statusCode, message } = OAuthResponse;
      if (!token && !statusCode && closed) {
        // Google login was unsuccessful
        dispatch(setIsLoading(false));
      } else if (statusCode === 200 && token && userDetails) {
        // Existing user logging in via Google OAuth
        // Set redux with user details from Google profile
        const cleanUserDetails = JSON.parse((userDetails as string).split('#')[0]);
        const {
          owned_account_id: account_id,
          avatar_url,
          email_address,
          first_name,
          google_id,
          last_name,
          phone_number,
          user_id,
        } = cleanUserDetails;

        dispatch(
          setUser({
            userId: account_id,
            avatarUrl: avatar_url,
            email: email_address,
            firstName: first_name,
            lastName: last_name,
            googleId: google_id,
            isNewUser: false,
          })
        );

        const verifySocialAuthResponse = await verifySocialAuth({
          deviceDetails,
          token,
          fingerprintData: fingerPrintData as ExtendedGetResult,
          user_id,
          account_id,
          phone_number,
          redirectFrom,
          redirectTo,
          signInMethod: SignInMethod.GOOGLE,
        });
        setFSUser(
          verifySocialAuthResponse.userId,
          email_address,
          verifySocialAuthResponse.firstName,
          verifySocialAuthResponse.lastName
        );

        if (verifySocialAuthResponse?.skip2FA) {
          // If we skipped 2fa, then log in the user.
          if (verifySocialAuthResponse.uri) {
            handleUserRedirect(verifySocialAuthResponse?.uri, !!verifySocialAuthResponse?.hasPowerTag);
          } else {
            throw new Error('Invalid redirect URL received.');
          }
        } else {
          // If we sent the 2fa, then go to 2fa screen.
          // Set store value to the masked phone number sent by Auth BE
          dispatch(
            setUser({
              phoneNumberFormatted: verifySocialAuthResponse?.phoneNumber,
              hasPowerTag: !!verifySocialAuthResponse.hasPowerTag,
            })
          );
          dispatch(
            setAuth({
              authCodeToken: verifySocialAuthResponse.token,
              isLoading: false,
              isTwilioVerifyUsed: verifySocialAuthResponse.isTwilioVerify,
            })
          );
          // 2fa was sent to user, so navigate to 2fa view.
          history.push('/login/enter-twofa-code');
        }
      } else if (statusCode === 200 && message?.includes('RegisterUser') && userDetails) {
        // New user registering via Google OAuth
        // Set redux with user details from Google profile
        const { avatar_url, email_address, first_name, google_id, last_name } = JSON.parse(userDetails);
        dispatch(
          setUser({
            avatarUrl: avatar_url,
            email: email_address,
            firstName: first_name,
            lastName: last_name,
            googleId: google_id,
            isNewUser: true,
          })
        );
        // Continue to register flow
        history.push('/login/phone-number');
      } else {
        throw 'Unable to fulfill social login request!';
      }
    } catch (error: any) {
      dispatch(setIsLoading(false));
      if (error?.name === 'HTTPError' && error?.response.status === 403) {
        throwAlert(error?.response.statusText, 'Unable to log in. Please contact support for more information.');
      } else {
        handleError(error);
      }
    }
  }

  return onSocialAuthCallback;
}
